import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, MailX, Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import {
  listSuppressedEmails,
  resubscribeEmail,
  addSuppressedEmail,
} from "@/lib/admin-email.functions";

export const Route = createFileRoute("/admin/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribes · Admin · WeLivdIt" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminUnsubscribePage,
});

type Item = {
  id: string;
  email: string;
  name: string | null;
  reason: string;
  created_at: string;
  category: "enrolled" | "lead";
  origin: string;
};

function AdminUnsubscribePage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Item[]>([]);
  const [stats, setStats] = useState({ total: 0, enrolled: 0, leads: 0, optOutRate: 0 });
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const list$ = useServerFn(listSuppressedEmails);
  const resub$ = useServerFn(resubscribeEmail);
  const add$ = useServerFn(addSuppressedEmail);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await list$();
      setItems(r.items as any);
      setStats(r.stats);
    } catch (e: any) {
      toast.error("Failed to load", { description: e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      const ok = data.session ? await hasAdminAccess(data.session) : false;
      if (!ok) {
        navigate({ to: "/auth" });
        return;
      }
      setAuthChecked(true);
      refresh();
    })();
  }, [navigate]);

  const doResub = async (email: string) => {
    setBusy(email);
    try {
      await resub$({ data: { email } });
      toast.success(`Re-subscribed ${email}`);
      await refresh();
    } catch (e: any) {
      toast.error("Failed", { description: e?.message });
    } finally {
      setBusy(null);
    }
  };

  const doAdd = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy("__add");
    try {
      await add$({ data: { email: newEmail.trim() } });
      toast.success(`Opted-out ${newEmail}`);
      setNewEmail("");
      await refresh();
    } catch (e: any) {
      toast.error("Failed", { description: e?.message });
    } finally {
      setBusy(null);
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-5 py-10 space-y-6">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <div className="mt-4 flex items-center gap-2">
          <MailX className="h-5 w-5 text-primary" />
          <h1 className="font-display text-3xl font-semibold">Unsubscribe Management</h1>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard label="Total Unsubscribes" value={stats.total} />
        <StatCard label="Unsubscribed Leads" value={stats.leads} />
        <StatCard label="Unsubscribed Enrolled" value={stats.enrolled} />
        <StatCard label="Opt-Out Rate" value={`${stats.optOutRate}%`} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row gap-3 sm:items-end">
        <label className="text-sm flex-1">
          <span className="text-xs font-medium text-foreground/70">Manually add opt-out</span>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="email@example.com"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={doAdd}
          disabled={busy === "__add"}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border bg-background text-sm font-semibold hover:bg-accent transition disabled:opacity-60"
        >
          {busy === "__add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add opt-out
        </button>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-10 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-foreground/60">No unsubscribes yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-foreground/60">
                <tr>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Origin</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{it.name || "—"}</div>
                      <div className="text-xs text-foreground/60">{it.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <CategoryBadge category={it.category} />
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground/70">
                      {format(parseISO(it.created_at), "dd MMM yyyy · HH:mm")}
                    </td>
                    <td className="px-4 py-3 text-xs text-foreground/70">{it.origin}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => doResub(it.email)}
                        disabled={busy === it.email}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border bg-background text-xs font-semibold hover:bg-accent transition disabled:opacity-60"
                      >
                        {busy === it.email ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" />
                        )}
                        Re-subscribe
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="text-[11px] uppercase tracking-wider font-bold text-foreground/60">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function CategoryBadge({ category }: { category: "enrolled" | "lead" }) {
  if (category === "enrolled") {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-0.5 text-[11px] font-semibold">
        Enrolled Student
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2.5 py-0.5 text-[11px] font-semibold">
      Unenrolled Lead
    </span>
  );
}
